<script setup lang="ts">
import {
  getPlayerSoundSettings,
  previewPlayerSound,
  setPlayerSoundVolume,
  setSoundVolume,
  subscribePlayerSoundSettings,
  type PlayerSoundID,
} from '@/lib/readyCheckAlert'
import type { PlayerWebPushState } from '@/lib/playerWebPush'
import { computed, onBeforeUnmount, ref } from 'vue'

const props = defineProps<{
  authenticated: boolean
  pushState: PlayerWebPushState
}>()

const emit = defineEmits<{
  enablePush: []
  disablePush: []
}>()

type SoundOption = {
  id: PlayerSoundID
  label: string
}

type SoundGroup = {
  id: string
  label: string
  sounds: readonly SoundOption[]
}

const soundGroups: readonly SoundGroup[] = [
  {
    id: 'matchmaking',
    label: 'Поиск и игра',
    sounds: [
      { id: 'readyFocused', label: 'Игра готова — активная вкладка' },
      { id: 'readyNoFocus', label: 'Игра готова — другая вкладка' },
      { id: 'accepted', label: 'Подтверждение игры' },
      { id: 'declined', label: 'Отказ от игры' },
      { id: 'connect', label: 'Подключение к матчу' },
      { id: 'searchStart', label: 'Начало поиска' },
      { id: 'searchCancel', label: 'Отмена поиска' },
    ],
  },
  {
    id: 'interface',
    label: 'Интерфейс',
    sounds: [
      { id: 'panelOpen', label: 'Открытие поиска' },
      { id: 'panelClose', label: 'Закрытие поиска' },
      { id: 'queueChange', label: 'Смена очереди' },
    ],
  },
  {
    id: 'social',
    label: 'Чат и социальное',
    sounds: [
      { id: 'chatMessage', label: 'Новое сообщение' },
      { id: 'socialNotification', label: 'Новое приглашение или заявка' },
    ],
  },
] as const

const settings = ref(getPlayerSoundSettings())
const unsubscribe = subscribePlayerSoundSettings(next => {
  settings.value = next
})

const pushStatusLabel = computed(() => {
  switch (props.pushState.status) {
    case 'on': return 'Включены'
    case 'checking': return 'Проверка…'
    case 'blocked': return 'Запрещены в браузере'
    case 'unsupported': return 'Не поддерживаются'
    case 'unavailable': return 'Недоступны на сервере'
    case 'error': return 'Ошибка синхронизации'
    default: return 'Выключены'
  }
})

const pushActionDisabled = computed(() => (
  props.pushState.busy ||
  props.pushState.status === 'unsupported' ||
  props.pushState.status === 'unavailable' ||
  props.pushState.status === 'blocked'
))

onBeforeUnmount(unsubscribe)

function updateMasterVolume(event: Event): void {
  const input = event.target as HTMLInputElement
  setSoundVolume(Number(input.value))
}

function updateSoundVolume(soundID: PlayerSoundID, event: Event): void {
  const input = event.target as HTMLInputElement
  setPlayerSoundVolume(soundID, Number(input.value))
}

function volumeLabel(volume: number): string {
  return `${Math.round(volume * 100)}%`
}
</script>

<template>
  <section
    class="player-sound-settings"
    aria-labelledby="player-sound-settings-title"
  >
    <div class="player-sound-settings-heading">
      <h2 id="player-sound-settings-title">Звуки</h2>
      <span>Общая и отдельная громкость</span>
    </div>

    <label class="player-sound-master" for="player-sound-master-volume">
      <span>Общая громкость</span>
      <strong>{{ volumeLabel(settings.master) }}</strong>
      <input
        id="player-sound-master-volume"
        type="range"
        min="0"
        max="1"
        step="0.05"
        :value="settings.master"
        @input="updateMasterVolume"
      />
    </label>

    <section
      v-if="authenticated"
      class="player-push-settings"
      aria-labelledby="player-push-settings-title"
    >
      <div class="player-push-settings-copy">
        <h3 id="player-push-settings-title">Системные уведомления</h3>
        <p>
          Ready-check и подключение к матчу даже после выгрузки
          вкладки. Звук системной карточки задают Chrome и Windows;
          звуки во вкладке и их громкость настраиваются выше.
        </p>
        <strong :class="{ active: pushState.active }">{{ pushStatusLabel }}</strong>
        <p v-if="pushState.status === 'blocked'" class="player-push-help">
          Разрешите уведомления для сайта в настройках Chrome.
        </p>
        <p v-else-if="pushState.error" class="player-push-help error">
          {{ pushState.error }}
        </p>
      </div>
      <button
        type="button"
        class="player-push-action"
        :class="{ active: pushState.active }"
        :disabled="pushActionDisabled"
        @click="pushState.active ? emit('disablePush') : emit('enablePush')"
      >
        {{ pushState.busy ? 'Подождите…' : pushState.active ? 'Выключить' : 'Включить' }}
      </button>
    </section>

    <div class="player-sound-list">
      <section
        v-for="group in soundGroups"
        :key="group.id"
        class="player-sound-group"
        :aria-labelledby="`player-sound-group-${group.id}`"
      >
        <h3 :id="`player-sound-group-${group.id}`">{{ group.label }}</h3>
        <div
          v-for="sound in group.sounds"
          :key="sound.id"
          class="player-sound-row"
          :data-sound-id="sound.id"
        >
          <label :for="`player-sound-volume-${sound.id}`">
            <span>{{ sound.label }}</span>
            <strong>{{ volumeLabel(settings.volumes[sound.id]) }}</strong>
          </label>
          <button
            type="button"
            class="player-sound-preview"
            :aria-label="`Прослушать: ${sound.label}`"
            title="Прослушать"
            @click="previewPlayerSound(sound.id)"
          >
            ▶
          </button>
          <input
            :id="`player-sound-volume-${sound.id}`"
            type="range"
            min="0"
            max="1"
            step="0.05"
            :value="settings.volumes[sound.id]"
            :aria-label="`Громкость: ${sound.label}`"
            @input="updateSoundVolume(sound.id, $event)"
          />
        </div>
      </section>
    </div>
  </section>
</template>

<style scoped>
.player-sound-settings {
  display: grid;
  min-width: 0;
  gap: 12px;
  color: #d1dbd9;
}

.player-sound-settings-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.player-sound-settings-heading h2,
.player-sound-group h3 {
  margin: 0;
  text-transform: uppercase;
}

.player-sound-settings-heading h2 {
  color: #e8eeee;
  font-size: 13px;
  letter-spacing: 1.2px;
}

.player-sound-settings-heading span {
  color: #7f8e91;
  font-size: 10px;
}

.player-sound-master {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 7px 12px;
  font-size: 11px;
  letter-spacing: 0.7px;
  text-transform: uppercase;
}

.player-sound-master strong,
.player-sound-row strong {
  color: #e9c46f;
  font-size: 11px;
}

.player-sound-master input,
.player-sound-row > input {
  width: 100%;
  accent-color: #d4a641;
}

.player-sound-master input {
  grid-column: 1 / -1;
}

.player-push-settings {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid rgb(92 122 130 / 0.42);
  background: rgb(10 18 21 / 0.72);
}

.player-push-settings-copy {
  display: grid;
  gap: 5px;
  min-width: 0;
}

.player-push-settings h3,
.player-push-settings p {
  margin: 0;
}

.player-push-settings h3 {
  color: #dbe8e8;
  font-size: 11px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
}

.player-push-settings p {
  color: #89999b;
  font-size: 10px;
  line-height: 1.35;
}

.player-push-settings strong {
  color: #b8c4c3;
  font-size: 10px;
  text-transform: uppercase;
}

.player-push-settings strong.active {
  color: #8ec9a7;
}

.player-push-settings .player-push-help.error {
  color: #d9a19b;
}

.player-push-action {
  min-width: 88px;
  padding: 8px 10px;
  border: 1px solid rgb(113 153 164 / 0.55);
  color: #d5e6e9;
  cursor: pointer;
  background: linear-gradient(180deg, #28383d, #152024);
  font: inherit;
  font-size: 10px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.player-push-action.active {
  border-color: rgb(95 151 118 / 0.62);
  color: #bfe5cd;
}

.player-push-action:disabled {
  cursor: default;
  opacity: 0.55;
}

.player-sound-list {
  display: grid;
  gap: 14px;
  max-height: min(430px, calc(100vh - 235px));
  padding-right: 5px;
  overflow: auto;
  scrollbar-color: #58666a #11191c;
  scrollbar-width: thin;
}

.player-sound-group {
  display: grid;
  gap: 8px;
}

.player-sound-group h3 {
  padding-bottom: 5px;
  border-bottom: 1px solid rgb(109 126 132 / 0.28);
  color: #8ea0a2;
  font-size: 10px;
  letter-spacing: 1px;
}

.player-sound-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 30px;
  align-items: center;
  gap: 5px 8px;
}

.player-sound-row label {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  color: #c7d1d0;
  font-size: 11px;
}

.player-sound-row label span {
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.player-sound-row > input {
  grid-column: 1 / -1;
}

.player-sound-preview {
  width: 30px;
  height: 26px;
  padding: 0;
  border: 1px solid rgb(101 123 132 / 0.48);
  color: #b9d7e3;
  cursor: pointer;
  background: linear-gradient(180deg, #263238, #12191c);
  font-size: 10px;
}

.player-sound-preview:hover,
.player-sound-preview:focus-visible {
  border-color: rgb(159 202 219 / 0.72);
  color: #effbff;
  filter: brightness(1.12);
}
</style>
